/**
 * Redirect for the pre-workspace URL shape.
 *
 * `/agents` and `/collections` carried the tenant in a query param — under two
 * different names, which is why switching between them used to lose it. Those
 * carry through into the workspace path rather than dropping on the floor.
 *
 * Keep for at least one release after the IA change ships; operators have
 * these bookmarked.
 */
import { Navigate, useLocation, useParams, type Params } from 'react-router-dom';

/**
 * `/agents?organization_id=X` → `/orgs/X/agents`
 * `/collections?org=X`        → `/orgs/X/collections`
 *
 * Without the param there is no tenant to scope to, so the operator lands on
 * the tenant list — the pick-an-organization step the old pages rendered
 * inline as an empty state.
 */
export function OrgScopedRedirect({ param, section }: { param: string; section: string }) {
  const { search } = useLocation();
  const orgId = new URLSearchParams(search).get(param);
  return <Navigate to={orgId ? `/orgs/${orgId}/${section}` : '/organizations'} replace />;
}

/**
 * `/organizations/:id` → `/orgs/:id/overview`
 *
 * The organization record and the tenant workspace were two pages; the
 * tenant home is both (Feature 70, D3). Bookmarks to the record land there.
 */
export function OrgRecordRedirect() {
  const { id = '' } = useParams<{ id: string }>();
  return <Navigate to={id ? `/orgs/${id}/overview` : '/organizations'} replace />;
}

/**
 * A moved address, its query kept: an open record, a tab, or the tour carries
 * over, and the new address's own query wins where both name a key. Feature
 * 74 moved Linkages and the trigger templates under Automation (D10).
 */
export function KeepQueryRedirect({
  to,
}: {
  to: string | ((params: Readonly<Params<string>>) => string);
}) {
  const { search } = useLocation();
  const params = useParams();
  const target = new URL(typeof to === 'string' ? to : to(params), 'http://console.local');
  new URLSearchParams(search).forEach((value, key) => {
    if (!target.searchParams.has(key)) target.searchParams.set(key, value);
  });
  return <Navigate to={target.pathname + target.search} replace />;
}
